import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Avatar,
  useTheme,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import {
  EmojiEmotions,
  Public,
  TrendingUp,
  Security,
  School,
  WorkspacePremium,
  ArrowForward,
  CheckCircle,
  Business,
  Shield,
  HeadsetMic,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const About = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const values = [
    { icon: <Public />, title: 'Global Mindset', desc: 'Thinking globally, acting locally', color: '#e60108' },
    { icon: <TrendingUp />, title: 'Innovation', desc: 'Pushing boundaries of technology', color: '#191919' },
    { icon: <Security />, title: 'Reliability', desc: 'Trust through consistency', color: '#e60108' },
    { icon: <EmojiEmotions />, title: 'Customer First', desc: 'Your success is our priority', color: '#a0a0a0' },
  ];

  const benefits = [
    { text: 'Uptime guaranteed', icon: <CheckCircle sx={{ fontSize: 18 }} /> },
    { text: 'ISO 27001 certified security', icon: <Shield sx={{ fontSize: 18 }} /> },
    { text: '24/7 multilingual support', icon: <HeadsetMic sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '60vh',
          minHeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Connecting the World.jpg"
          alt="Connecting the World"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="About Us"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                display: 'inline-flex',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Building the Future of
              <br />
              <Box component="span" sx={{ color: '#e60108' }}>
                Global Communication
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              A new generation telecommunications company, built to connect communities
              and businesses across Southern Africa with reliable, modern infrastructure.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Our Story Section - Centered, No Image */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Story"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
              Connecting the World
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                lineHeight: 1.9,
                fontSize: '1.05rem',
                maxWidth: 780,
                mx: 'auto',
                mb: 4,
              }}
            >
              We are a new generation communications company with a bold vision — to
              build modern, reliable, and affordable connectivity across Southern
              Africa. From our founding, we have focused on laying the foundation for
              a world-class network, starting with towers, and data center
              infrastructure that will grow with the communities we serve. Every day
              we are expanding, investing, and building toward a connected future.
            </Typography>

            <Stack
              spacing={2}
              sx={{ mt: 3, maxWidth: 500, mx: 'auto', alignItems: 'center' }}
            >
              {benefits.map((item, index) => (
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent="center"
                  key={index}
                  sx={{ width: '100%' }}
                >
                  <Box sx={{ color: '#e60108', display: 'flex' }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#191919' }}>
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ display: 'inline-block', marginTop: 32 }}
            >
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/contact')}
                sx={{
                  bgcolor: '#e60108',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    bgcolor: '#b80006',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Learn More About Us
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Mission & Vision - Centered, No Images */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
          >
            {/* Mission Card */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Card
                  sx={{
                    width: '100%',
                    minHeight: 340,
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: '#e60108',
                      margin: '0 auto 24px',
                    }}
                  >
                    <School sx={{ fontSize: 40, color: '#fefefe' }} />
                  </Avatar>
                  <Typography
                    variant="h4"
                    gutterBottom
                    fontWeight="800"
                    sx={{ color: '#191919', textAlign: 'center' }}
                  >
                    Our Mission
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#a0a0a0',
                      lineHeight: 1.8,
                      textAlign: 'center',
                      maxWidth: 420,
                      mx: 'auto',
                    }}
                  >
                    To democratize global connectivity by providing accessible, reliable,
                    and cutting-edge communication solutions that empower businesses.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            {/* Vision Card */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Card
                  sx={{
                    width: '100%',
                    minHeight: 340,
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: '#191919',
                      margin: '0 auto 24px',
                    }}
                  >
                    <WorkspacePremium sx={{ fontSize: 40, color: '#fefefe' }} />
                  </Avatar>
                  <Typography
                    variant="h4"
                    gutterBottom
                    fontWeight="800"
                    sx={{ color: '#191919', textAlign: 'center' }}
                  >
                    Our Vision
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#a0a0a0',
                      lineHeight: 1.8,
                      textAlign: 'center',
                      maxWidth: 420,
                      mx: 'auto',
                    }}
                  >
                    A world where distance doesn't matter - where seamless communication
                    connects every person, business, and community.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Values - Centered */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Core Values"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Our Core Values
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              The principles that guide everything we do
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center" alignItems="stretch">
            {values.map((value, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  style={{ width: '100%', display: 'flex' }}
                >
                  <Card
                    sx={{
                      width: '100%',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        bgcolor: value.color + '15',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        color: value.color,
                      }}
                    >
                      <Box sx={{ fontSize: 35, display: 'flex' }}>{value.icon}</Box>
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="bold"
                      sx={{ color: '#191919', textAlign: 'center' }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#a0a0a0', textAlign: 'center' }}
                    >
                      {value.desc}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <CTASection
        title="Join Our Journey"
        subtitle="Be part of the global communication revolution"
        buttonText="Work With Us"
        buttonLink="/careers"
      />
    </Box>
  );
};

export default About;